import React, { useState, useEffect, useMemo } from 'react';
import Card from '../components/common/Card';
import ActionMenu from '../components/common/ActionMenu';
import { CheckCircle2, Pencil, Power, Trash2 } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const Courses = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [examBodies, setExamBodies] = useState([]);
  const [levels, setLevels] = useState([]);
  const [filteredLevels, setFilteredLevels] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    department_id: '',
    exam_body_id: '',
    level_id: ''
  });
  const [loading, setLoading] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: '',
    department_id: '',
    exam_body_id: '',
    level_id: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Check permissions
  const canManage = user?.role === 'admin' || user?.role === 'editor';
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const isViewer = user?.role === 'viewer';

  // Get unique levels from courses for filter
  const uniqueLevels = useMemo(() => {
    const levelMap = new Map();
    courses.forEach(course => {
      if (course.level_id && !levelMap.has(course.level_id)) {
        levelMap.set(course.level_id, {
          id: course.level_id,
          level_number: course.level_number,
          level_name: course.level_name,
          display_name: course.level_number ? `Level ${course.level_number}` : course.level_name
        });
      }
    });
    return Array.from(levelMap.values()).sort((a, b) => {
      if (a.level_number && b.level_number) return a.level_number - b.level_number;
      if (a.level_number) return -1;
      if (b.level_number) return 1;
      return (a.level_name || '').localeCompare(b.level_name || '');
    });
  }, [courses]);

  // Get unique exam bodies from courses for filter
  const uniqueExamBodies = useMemo(() => {
    const examMap = new Map();
    courses.forEach(course => {
      if (course.exam_body_id && !examMap.has(course.exam_body_id)) {
        examMap.set(course.exam_body_id, {
          id: course.exam_body_id,
          name: course.exam_body_name
        });
      }
    });
    return Array.from(examMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [courses]);

  useEffect(() => {
    fetchData();
  }, []);

  // Filter levels when exam body changes
  useEffect(() => {
    if (formData.exam_body_id && levels.length > 0) {
      const filtered = levels.filter(level => level.exam_body_id === parseInt(formData.exam_body_id));
      setFilteredLevels(filtered);
      // Reset level selection when exam body changes
      setFormData(prev => ({ ...prev, level_id: '' }));
    } else {
      setFilteredLevels([]);
    }
  }, [formData.exam_body_id, levels]);

  // Apply filters
  useEffect(() => {
    let filtered = [...courses];

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(course =>
        course.name?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.department_id) {
      filtered = filtered.filter(course => course.department_id === parseInt(filters.department_id));
    }

    if (filters.exam_body_id) {
      filtered = filtered.filter(course => course.exam_body_id === parseInt(filters.exam_body_id));
    }

    if (filters.level_id) {
      filtered = filtered.filter(course => course.level_id === parseInt(filters.level_id));
    }

    setFilteredCourses(filtered);
    setCurrentPage(1);
  }, [courses, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesRes, deptsRes, examRes, levelsRes] = await Promise.all([
        api.get('/courses'),
        api.get('/departments'),
        api.get('/exam-bodies'),
        api.get('/course-levels')
      ]);

      setCourses(coursesRes.data);
      setFilteredCourses(coursesRes.data);
      setDepartments(deptsRes.data);
      setExamBodies(examRes.data);

      // Flatten levels from all exam bodies
      const allLevels = [];
      levelsRes.data.forEach(examBody => {
        if (examBody.levels && examBody.levels.length > 0) {
          examBody.levels.forEach(level => {
            allLevels.push({
              id: level.id,
              exam_body_id: examBody.exam_body_id,
              exam_body_name: examBody.exam_body_name,
              exam_body_code: examBody.exam_body_code,
              level_number: level.level_number,
              level_name: level.level_name,
              sort_order: level.sort_order,
              display_name: level.level_number ? `Level ${level.level_number}` : level.level_name
            });
          });
        }
      });
      setLevels(allLevels);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      department_id: '',
      exam_body_id: '',
      level_id: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canManage) {
      toast.error('You do not have permission to manage courses');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Course name is required');
      return;
    }

    setLoading(true);
    try {
      if (editingCourse) {
        await api.put(`/courses/${editingCourse.id}`, formData);
        toast.success('Course updated successfully');
      } else {
        await api.post('/courses', formData);
        toast.success('Course created successfully');
      }
      setShowForm(false);
      setEditingCourse(null);
      setFormData({
        name: '',
        department_id: '',
        exam_body_id: '',
        level_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving course');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (course) => {
    if (!canEdit) {
      toast.error('You do not have permission to edit courses');
      return;
    }
    setEditingCourse(course);
    setFormData({
      name: course.name,
      department_id: course.department_id,
      exam_body_id: course.exam_body_id,
      level_id: course.level_id
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!canManage) {
      toast.error('You do not have permission to delete courses');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this course?')) return;

    setLoading(true);
    try {
      await api.delete(`/courses/${id}`);
      toast.success('Course deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting course');
    } finally {
      setLoading(false);
    }
  };

  const getLevelDetails = (level) => {
    if (level.level_number && level.level_name) {
      return `${level.level_name} (Level ${level.level_number})`;
    } else if (level.level_number) {
      return `Level ${level.level_number}`;
    } else if (level.level_name) {
      return level.level_name;
    }
    return 'Unknown';
  };

  const getLevelBadgeClass = (levelNumber, levelName) => {
    if (levelNumber === 6) return 'bg-purple-100 text-purple-800';
    if (levelNumber === 5) return 'bg-blue-100 text-blue-800';
    if (levelNumber === 4) return 'bg-green-100 text-green-800';
    if (levelNumber === 3) return 'bg-yellow-100 text-yellow-800';
    if (levelName === 'Diploma') return 'bg-indigo-100 text-indigo-800';
    if (levelName === 'Certificate') return 'bg-cyan-100 text-cyan-800';
    if (levelName === 'Artisan') return 'bg-orange-100 text-orange-800';
    if (levelName === 'First Class') return 'bg-amber-100 text-amber-800';
    if (levelName === 'Pass') return 'bg-lime-100 text-lime-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCourses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCourses.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">All Courses</h1>
          <p className="text-gray-600 mt-1">Manage courses across all departments</p>
        </div>
        {canManage && (
          <Button onClick={() => {
            setEditingCourse(null);
            setFormData({
              name: '',
              department_id: '',
              exam_body_id: '',
              level_id: ''
            });
            setShowForm(true);
          }}>
            + Add New Course
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Input
              label="Search Courses"
              name="searchTerm"
              value={filters.searchTerm}
              onChange={handleFilterChange}
              placeholder="🔍 Search by course name..."
              className="w-full"
              style={{ height: '42px' }}
            />
          </div>
          
          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Department</label>
            <select
              className="input-field w-full h-10"
              name="department_id"
              value={filters.department_id}
              onChange={handleFilterChange}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Exam Body</label>
            <select
              className="input-field w-full h-10"
              name="exam_body_id"
              value={filters.exam_body_id}
              onChange={handleFilterChange}
            >
              <option value="">All Exam Bodies</option>
              {uniqueExamBodies.map(body => (
                <option key={body.id} value={body.id}>{body.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label text-sm font-medium text-gray-700 mb-1 block">Level</label>
            <select
              className="input-field w-full h-10"
              name="level_id"
              value={filters.level_id}
              onChange={handleFilterChange}
            >
              <option value="">All Levels</option>
              {uniqueLevels.map(level => (
                <option key={level.id} value={level.id}>
                  {level.display_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mt-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing {filteredCourses.length} courses
          </div>
          {(filters.searchTerm || filters.department_id || filters.exam_body_id || filters.level_id) && (
            <Button variant="outline" onClick={clearFilters} className="text-sm h-8 px-3">
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {showForm && canManage && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingCourse ? 'Edit Course' : 'Add New Course'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <Input
                label="Course Name *"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Electrical Engineering Technology"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="label">Department *</label>
                <select
                  className="input-field"
                  value={formData.department_id}
                  onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                  required
                >
                  <option value="">-- Select Department --</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="label">Exam Body *</label>
                <select
                  className="input-field"
                  value={formData.exam_body_id}
                  onChange={(e) => setFormData({...formData, exam_body_id: e.target.value})}
                  required
                >
                  <option value="">-- Select Exam Body --</option>
                  {examBodies.map(body => (
                    <option key={body.id} value={body.id}>
                      {body.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4 md:col-span-2">
                <label className="label">Course Level *</label>
                <select
                  className="input-field"
                  value={formData.level_id}
                  onChange={(e) => setFormData({...formData, level_id: e.target.value})}
                  required
                  disabled={!formData.exam_body_id}
                >
                  <option value="">-- Select Level --</option>
                  {filteredLevels.map(level => (
                    <option key={level.id} value={level.id}>
                      {getLevelDetails(level)}
                    </option>
                  ))}
                </select>
                {!formData.exam_body_id && (
                  <p className="text-xs text-gray-500 mt-1">Please select an exam body first</p>
                )}
                {formData.exam_body_id && filteredLevels.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No levels found for this exam body. Please add levels in Course Levels Management first.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingCourse ? 'Update Course' : 'Save Course')}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowForm(false);
                setEditingCourse(null);
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Results Count */}
      <div className="mb-4 flex justify-between items-center">
        <p className="text-sm text-gray-600">
          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCourses.length)} of {filteredCourses.length} courses
        </p>
      </div>

      {/* Courses Table - Single row with horizontal scroll */}
      <div className="overflow-x-auto shadow rounded-lg">
        <table className="min-w-[1000px] bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">#</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exam Body</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  No courses found. Click "Add New Course" to create one.
                </td>
              </tr>
            ) : (
              currentItems.map((course, index) => (
                <tr key={course.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 sticky left-0 bg-white">
                    {indexOfFirstItem + index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{course.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{course.department_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getLevelBadgeClass(course.level_number, course.level_name)}`}>
                      {course.level_number ? `Level ${course.level_number}` : course.level_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{course.exam_body_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <ActionMenu
                      label={`Actions for ${course.name || 'course'}`}
                      items={[
                        {
                          label: 'Edit',
                          icon: Pencil,
                          onClick: () => handleEdit(course),
                          disabled: !canEdit,
                        },
                        {
                          label: 'Delete',
                          icon: Trash2,
                          variant: 'danger',
                          onClick: () => handleDelete(course.id),
                          disabled: !canManage,
                        },
                      ]}
                    />
                    {isViewer && (
                      <span className="ml-2 text-xs text-ink-400">View only</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-6">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
          >
            Previous
          </button>

          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded ${
                    currentPage === pageNum
                      ? 'bg-secondary text-gray-900 font-semibold'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Courses;
